// A first challenge done before registration, held until there is an account to attach it to.
//
// WHY THIS EXISTS (2026-09-17, Mr. Chen item 4).
//
// The first challenge now runs signed-out, and `create_flare_session` refuses to store
// anything not confirmed 13+ — "the first Challenge is ephemeral and never reaches here."
// Asking for an age before the challenge is what item 4 rules out, so nothing is written
// while the learner is anonymous.
//
// That left the session simply lost at registration, and the door copy promises otherwise:
// "Create an account and today's counts as day one." So the payload is held here and posted
// by the flush component at the first moment a write is permitted.
//
// ── REWRITTEN 2026-09-18 (KAN-491). The original lost every session in production. ────────
//
// It held the payload in `sessionStorage`, which is PER TAB. Production has
// `REQUIRE_EMAIL_VERIFICATION=true`, so registration cannot finish in the tab where the
// challenge was taken: the learner leaves, opens their mail, and clicks a link that lands in
// a different tab. `sessionStorage` there is empty, the flush finds nothing, and the session
// dies with the original tab. Silently — a missing key is indistinguishable from a learner
// who never took the challenge.
//
// Measured, not theorised: a real registration on 2026-09-18 produced no row at all, and the
// dashboard correctly reported "0 of 1 done".
//
// The two facts were each fine alone. Nobody put them together, and the runbook said
// verification was off (it documented `REQUIRE_EMAIL_VERIFICATION=False`; production has been
// `true` throughout).
//
// THREE CHANGES, and the second and third are defects the first would have left in place:
//
//   1. localStorage, not sessionStorage. Survives the tab change that verification forces.
//      This reverses a deliberate choice — the original note said a session abandoned without
//      registering "should not sit on a shared machine indefinitely", which was right. So the
//      hold now CARRIES A TTL and expires on read. Bounded, not indefinite.
//
//   2. Read and clear were one operation, and the clear happened BEFORE the post. A failed
//      post therefore destroyed the payload it failed to send. They are now separate: the
//      caller clears on success, and a transient failure leaves the hold in place to be
//      retried on the next load, bounded by the TTL.
//
//   3. `classifyFailure` distinguishes a REFUSAL from a FAILURE. The original comment worried
//      about retrying "forever against an age gate that may refuse it" — correct, and the
//      answer is not to discard everything on the first error. A 4xx means the server decided;
//      retrying cannot change it, so the hold is dropped. A network error or a 5xx means we
//      never got an answer, which is not the same thing and is worth retrying.
//
// STILL OPEN, and it needs a decision rather than code (see KAN-491): a learner whose mail
// client opens the verification link in a DIFFERENT BROWSER is still lost, because
// localStorage is per origin per browser. Fixing that means storing the anonymous session
// server-side and claiming it via `/link` — which is what that endpoint exists for — and
// that runs into the age gate, since the server currently stores an anonymous session only
// after a 13+ assertion. Moving the age question earlier, or holding an unattributed session
// before it, is a product and data-governance call.

// ── TWO MODES (KAN-491, 2026-09-18) ───────────────────────────────────────────────────────
//
// POST  — nothing is on the server. The whole payload is held here and posted once an
//         account exists. This is the original path and remains the fallback.
//
// CLAIM — the learner asserted 13+ at the doors, so the session was stored anonymously
//         straight away and only its ID is held. The account claims it later via /link.
//
// CLAIM is what makes the cross-browser case work at all. POST cannot: the payload lives in
// one browser's storage, and a learner who opens the confirmation mail on their phone is
// signing in somewhere that has never seen it. With the session already on the server, the
// ID is the only thing that has to survive — and if even that is lost, the session still
// exists and can be reunited later rather than being gone.
//
// The age question is what unlocks this. `create_flare_session` refuses to store anything
// not confirmed 13+, which is why nothing could be written before the account existed. Asking
// once at the doors — AFTER the challenge, which is what item 4 requires — satisfies the gate
// without putting a form in front of the first experience.
export const MODE_POST = 'post';
export const MODE_CLAIM = 'claim';

const KEY = 'novara.flare.pending.v1';

/** How long a held session stays claimable. Long enough to read an email and come back;
 *  short enough that an abandoned challenge does not linger on a shared machine. */
export const HOLD_TTL_MS = 24 * 60 * 60 * 1000;

/** Hold an anonymous session's final payload until an account exists (MODE_POST). */
export function hold(payload) {
  try {
    window.localStorage.setItem(
      KEY,
      JSON.stringify({ at: new Date().toISOString(), mode: MODE_POST, payload })
    );
    return true;
  } catch {
    // Storage unavailable. The session is lost, which is exactly what happened before
    // this file existed — a degradation, not a new failure.
    return false;
  }
}

/**
 * Hold only the ID of a session already stored on the server (MODE_CLAIM).
 *
 * Replaces any POST-mode hold: once the server has the session, keeping a second copy of the
 * payload in the browser would be a second source of truth for the same thing, and the one
 * in the browser is the one that can go stale.
 */
export function holdClaim(flareSessionId) {
  if (!flareSessionId) return false;
  try {
    window.localStorage.setItem(
      KEY,
      JSON.stringify({ at: new Date().toISOString(), mode: MODE_CLAIM,
                       payload: { flare_session_id: flareSessionId } })
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * Read WITHOUT clearing. Returns `{ mode, payload }`, or null if nothing is held, it is
 * unreadable, or it has expired.
 *
 * Deliberately not read-and-clear: the caller clears only once the server has accepted it.
 * The previous version cleared first, so a failed post destroyed the payload it failed to
 * send — and the learner's first challenge was gone with nothing recorded anywhere.
 */
export function read() {
  let raw = null;
  try {
    raw = window.localStorage.getItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed = null;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clear();
    return null;
  }
  // Guard the shape rather than trusting storage: this value survives navigation and a tab
  // close now, and anything could have written to the key. A malformed hold must not break
  // registration.
  if (!parsed || typeof parsed !== 'object' || !parsed.payload) {
    clear();
    return null;
  }

  const at = Date.parse(parsed.at);
  // An unparseable timestamp is treated as expired rather than as fresh. The failure mode of
  // guessing "fresh" is an unbounded hold, which is the property the TTL exists to prevent.
  if (!Number.isFinite(at) || Date.now() - at > HOLD_TTL_MS) {
    clear();
    return null;
  }
  // A hold written before modes existed has no `mode` and is a payload to post. Defaulting
  // to POST rather than CLAIM is the safe direction: posting a session the server already
  // has is idempotent on flare_session_id, whereas claiming one it does not have 404s.
  return { mode: parsed.mode === MODE_CLAIM ? MODE_CLAIM : MODE_POST, payload: parsed.payload };
}

/** Drop the hold. Called on success, on refusal, and on anything malformed or expired. */
export function clear() {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* storage unavailable; nothing to clear */
  }
}

/**
 * Should a failed post discard the held session, or keep it for another try?
 *
 * REFUSED  the server answered and said no (4xx) — an age gate, a validation error, a
 *          duplicate. Retrying cannot change the answer, so stop holding it.
 * RETRY    no answer, or the server failed (network error, 5xx). We do not know whether the
 *          write happened and we certainly do not know that it was refused. Keep it; the TTL
 *          bounds how long this can go on.
 */
export const REFUSED = 'refused';
export const RETRY = 'retry';

// A 403 from the age gate is TWO different answers wearing one status code, and treating
// them alike is what made this bug permanent.
//
//   "age not on file"          the account has no birth_year. Registration does not ask for
//                              one — ONBOARDING does, and that happens after. So this is a
//                              NOT-YET, and it stops being true the moment the learner
//                              finishes onboarding. Retrying is the whole point.
//
//   "age not confirmed 13+"    the account has a birth year and it is under 13. This is a
//                              settled product and legal decision and it will never change
//                              on a retry. Drop it, and drop it permanently.
//
// Matched on the server's own wording rather than on status, because the status cannot tell
// them apart. If these strings change server-side this silently degrades to "retry", which
// is the safe direction: a retried write that the gate refuses again costs one request,
// where a wrongly-discarded one costs the learner their first challenge.
const AGE_NOT_ON_FILE = /age not on file/i;

export function classifyFailure(error) {
  const status = error && error.response && error.response.status;
  const detail = (error && error.response && error.response.data
    && (error.response.data.detail || error.response.data.message)) || '';

  if (status === 403 && AGE_NOT_ON_FILE.test(String(detail))) return RETRY;
  if (typeof status === 'number' && status >= 400 && status < 500) return REFUSED;
  return RETRY;
}

/** Testing/diagnostics only — never called from the product path. Does not expire or clear. */
export function peek() {
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export const PENDING_KEY = KEY;
